import React from 'react';

/** One selectable segment. */
export interface SegmentedOption<T extends string> {
  /** Value emitted when this segment is selected. */
  value: T;
  /** Visible label, pre-rendered and never swapped on click. */
  label: React.ReactNode;
}

/** Props for {@link SegmentedControl}. */
export interface SegmentedControlProps<T extends string> {
  /** Two or more equal-width options. */
  options: SegmentedOption<T>[];
  /** Currently selected value. */
  value: T;
  /** Called with the newly selected value when a segment is clicked. */
  onChange: (value: T) => void;
  /** Accessible label for the control group. */
  ariaLabel: string;
  /** Extra classes applied to the track. */
  className?: string;
  /** Visual density. Defaults to 'md'. */
  size?: 'sm' | 'md';
}

/**
 * Brand slide transition for the single indicator: the one easing curve at the
 * indicator-slide duration, with literal fallbacks if the tokens are absent.
 */
const SLIDE_TRANSITION = 'transform var(--dur-slide, 0.5s) var(--ease, cubic-bezier(0.16, 1, 0.3, 1))';

/**
 * A segmented (pill) control whose selection indicator physically slides as one
 * element via `transform: translateX`, never a per-item repaint. All labels are
 * pre-rendered; clicking a segment calls `onChange` with its value.
 *
 * Equal-width (`flex-1`) items let the indicator land exactly on each segment
 * via `translateX(activeIndex * 100%)`, so no measurement is needed.
 *
 * @param props See {@link SegmentedControlProps}.
 * @returns The segmented control element.
 */
export function SegmentedControl<T extends string>({
  options,
  value,
  onChange,
  ariaLabel,
  className,
  size = 'md',
}: SegmentedControlProps<T>): React.ReactElement {
  const rawIndex = options.findIndex((o) => o.value === value);
  const activeIndex = rawIndex < 0 ? 0 : rawIndex;
  const sizeClasses = size === 'sm' ? 'px-3 py-1' : 'px-4 py-1.5';

  return (
    <div
      role="group"
      aria-label={ariaLabel}
      className={`relative inline-flex p-1 bg-surface rounded-xl border border-border ${className ?? ''}`.trim()}
    >
      {/* Single sliding indicator: one item wide, moves via transform only. */}
      <span
        data-testid="segmented-indicator"
        aria-hidden="true"
        className="absolute top-1 bottom-1 left-1 z-0 bg-volt rounded-lg"
        style={{
          width: `calc((100% - 0.5rem) / ${options.length})`,
          transform: `translateX(${activeIndex * 100}%)`,
          transition: SLIDE_TRANSITION,
        }}
      />
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            aria-pressed={selected}
            onClick={() => onChange(option.value)}
            className={`relative z-10 flex-1 whitespace-nowrap text-sm font-medium rounded-lg transition-colors ${sizeClasses} ${
              selected ? 'text-accent-foreground' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
}
